/* Transfermarkt-Link eines Spielers — Prüfung als reine Funktion, kein DOM.
 *
 * Der Link steht in player.transfermarktUrl (Datenblock). Angezeigt UND gepflegt wird er seit 2026-10-07
 * nur bei transferierten Spielern, in Player Info (SectorPass.vue, Bearbeiten-Ansicht; Schreibrecht auf
 * players). Gäste lesen ihn über die Spalte transfermarkt_url der View players_public.
 *
 * Regel für Eingaben (Entscheidung Bastian, 2026-10-04):
 *   - nur https:// (kein http, kein javascript:, kein data:, keine Adresse ohne Schema)
 *   - der Host muss Transfermarkt sein: transfermarkt.<Endung> mit beliebiger Subdomain
 *     davor (www., us., …) — z. B. transfermarkt.com, transfermarkt.de, transfermarkt.co.uk,
 *     transfermarkt.com.tr. Ein Host, der nur "transfermarkt" IRGENDWO enthält
 *     (transfermarkt.evil.com, eviltransfermarkt.com, transfermarkt.com.evil.org), gilt NICHT.
 *   - keine Zugangsdaten (https://nutzer:passwort@…) und kein eigener Port
 *
 * Dieselbe Prüfung entscheidet beim Anzeigen, ob ein gespeicherter Wert als Link geöffnet
 * werden darf (safeTransfermarktHref): Altwerte, die diese Regel verletzen, erscheinen nur
 * als Text, nie als anklickbarer Link.
 */

/* Endung nach "transfermarkt.": zwei oder drei Buchstaben (com, de, it, …), co.uk, oder com.xx
   (Länder wie com.tr, com.br). Alles Weitere (transfermarkt.evil.com) passt nicht. */
const HOST = /(^|\.)transfermarkt\.(?:[a-z]{2,3}|co\.uk|com\.[a-z]{2})$/;

/** Fehlerschlüssel unter material.tmError.* in den Sprachdateien */
export const TM_ERRORS = ['empty', 'notHttps', 'notTransfermarkt', 'invalid'];

/**
 * @param {unknown} raw  Eingabe (wird getrimmt)
 * @returns {{ ok: true, url: string } | { ok: false, error: 'empty'|'notHttps'|'notTransfermarkt'|'invalid' }}
 */
export function validateTransfermarktUrl(raw) {
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (!text) return { ok: false, error: 'empty' };

  let url;
  try {
    url = new URL(text);
  } catch {
    /* Adresse ohne Schema ("www.transfermarkt.de/…") oder kaputt */
    return { ok: false, error: /^[a-z][a-z0-9+.-]*:/i.test(text) ? 'invalid' : 'notHttps' };
  }

  if (url.protocol !== 'https:') return { ok: false, error: 'notHttps' };
  if (url.username || url.password || url.port) return { ok: false, error: 'invalid' };
  if (!HOST.test(url.hostname.toLowerCase())) return { ok: false, error: 'notTransfermarkt' };
  return { ok: true, url: url.href };
}

/**
 * Adresse, die als Link geöffnet werden darf, sonst null (dann nur als Text anzeigen).
 * Gespeichert wird, was der Nutzer eingegeben hat bzw. validateTransfermarktUrl normalisiert
 * hat; Altwerte aus der Zeit ohne Prüfung können alles Mögliche enthalten.
 */
export function safeTransfermarktHref(value) {
  const r = validateTransfermarktUrl(value);
  return r.ok ? r.url : null;
}

/** Attribute für den Link: neuer Tab, ohne Verbindung zum öffnenden Fenster und ohne Referrer. */
export const TM_LINK_ATTRS = Object.freeze({ target: '_blank', rel: 'noopener noreferrer' });

/**
 * Eingabefeld in Player Info (Bearbeiten-Ansicht): Was passiert mit dem Text?
 * Leer = Link entfernen (ok, url ''); sonst gilt dieselbe Prüfung wie bei validateTransfermarktUrl, und es
 * wird der geprüfte, normalisierte Link übernommen. Ungültig: nichts übernehmen, Fehlerschlüssel melden.
 * @returns {{ ok: true, url: string } | { ok: false, error: string }}
 */
export function resolveTransfermarktInput(raw) {
  if (raw == null || String(raw).trim() === '') return { ok: true, url: '' };
  return validateTransfermarktUrl(raw);
}
